import { datadogRum } from '@datadog/browser-rum';

/**
 * FFH-1558 noise filter. These messages carry no actionable signal and
 * were drowning out real errors in error tracking:
 * - "Script error." is the browser's redacted stand-in for exceptions in
 *   cross-origin scripts; there is no message or stack to act on.
 * - "Can't find variable: EmptyRanges" comes from a browser extension,
 *   not our code.
 */
const KNOWN_NOISE_PATTERNS: RegExp[] = [
  /^(Uncaught ")?Script error\."?$/,
  /Can't find variable: EmptyRanges/
];

const isKnownNoise = (message: string | undefined): boolean =>
  Boolean(message) &&
  KNOWN_NOISE_PATTERNS.some((pattern) => pattern.test(message as string));

/**
 * FFH-2769. A Norwegian assistive reading and writing extension (the module
 * names match Lingdys) has its content script ask its background worker for
 * state and give up with `throw "Request timeout <name>"` when it does not
 * answer. One session can produce well over a hundred of these in a minute,
 * which at a traffic trough is enough to warn the browser error monitor.
 *
 * The provenance filter below cannot catch it: the extension throws a bare
 * string rather than an Error, so the SDK records no stack at all, only its
 * `No stack, consider using an instance of Error` placeholder. The stack is
 * exactly what this check keys on instead. A timeout of ours would be an Error
 * instance carrying a frame from our asset host, and survives. Nothing in this
 * repo throws that message either way. Over the 30 days to 27 Aug 2026 every
 * one of the 2,301 events was stackless and every one came from Norway.
 */
const STRING_THROW_STACK_PLACEHOLDER =
  'No stack, consider using an instance of Error';
const EXTENSION_REQUEST_TIMEOUT_PATTERN =
  /^(Uncaught ")?Request timeout \S+"?$/;

const isStacklessExtensionTimeout = (
  message: string | undefined,
  stack: string | undefined
): boolean =>
  Boolean(message) &&
  EXTENSION_REQUEST_TIMEOUT_PATTERN.test(message as string) &&
  (!stack || stack.startsWith(STRING_THROW_STACK_PLACEHOLDER));

/**
 * FFH-2483 provenance filter. An error thrown inside a browser extension is
 * not our code, was not loaded by us, and cannot be fixed from this repo, but
 * it still lands in RUM against the page the user happened to be on.
 *
 * Measured over the 24 hours to 22 Aug 2026: 5,449 of 25,085 RUM errors came
 * from extension stacks, and one extension alone produced 4,601 of them
 * across just 40 sessions. That was enough to push the share of views
 * carrying an error from a ~1.1% baseline through the 2% and 3% thresholds of
 * the browser error rate monitor repeatedly.
 *
 * This filters on provenance rather than on the message, because the messages
 * are not distinctive: "Failed to fetch" appeared 636 times from extension
 * stacks and 1,487 times overall in that window, so a message pattern for it
 * would have discarded real network failures too.
 *
 * Dropping the event here also keeps it out of `@view.error.count`. The SDK
 * only increments that count from `RUM_EVENT_COLLECTED`, which is notified
 * after `beforeSend` has accepted the event (see `assembly.js` and
 * `trackEventCounts.js` in @datadog/browser-rum-core), so a discarded error
 * never reaches the view's tally. That is what makes the monitor's ratio
 * trustworthy, and it is not documented, so re-check it on an SDK major.
 */
const EXTENSION_STACK_PROTOCOLS = [
  'chrome-extension://',
  'moz-extension://',
  'safari-web-extension://',
  'safari-extension://'
];

/**
 * The SDK normalises every browser's stack into `\n  at {func} @ {url}` via
 * `toStackTraceString`, so this one pattern matches frames from Chrome,
 * Firefox and Safari alike.
 */
const STACK_FRAME_PATTERN = /^\s*at\s/;

/**
 * Only the topmost frame is tested: that is the frame that threw. An
 * extension which patches `fetch` or `addEventListener` can appear lower in
 * the stack of a genuine fault of ours, and discarding those would hide real
 * bugs.
 */
const isThrownByExtension = (stack: string | undefined): boolean => {
  const topFrame = stack
    ?.split('\n')
    .find((line) => STACK_FRAME_PATTERN.test(line));

  return Boolean(
    topFrame &&
      EXTENSION_STACK_PROTOCOLS.some((protocol) => topFrame.includes(protocol))
  );
};

/**
 * FFH-2860. In-app browsers — the WebView Instagram, Facebook, Twitter,
 * Snapchat and Zalo render links in rather than handing off to the real
 * browser — inject their own bridge scripts into every page they show. Those
 * scripts expect a host global that is not there in a plain page context, and
 * throw when it is missing.
 *
 * The provenance filter above cannot catch them. A bridge script is injected
 * as an ordinary page script, so its stack frames carry no
 * `chrome-extension://` protocol; as far as the stack is concerned it is our
 * page throwing. Verified by browser over the 14 days to 3 Sep 2026: every
 * occurrence of each identifier below came from the host app named beside it,
 * and none from a normal browser.
 *
 *   _AutofillCallbackHandler   Facebook 106, Instagram 295
 *   454: Handling is disabled  Instagram 354, Facebook 108, WKWebView 17
 *   CONFIG                     Twitter 216
 *   currentInset               Twitter 71
 *   zaloJSV2                   WKWebView (Zalo) 46
 *   SCDynimacBridge            Snapchat 18
 *
 * Matched on the IDENTIFIER, not on a general `Can't find variable:` pattern.
 * A missing global of ours is a real bug and has to keep reporting, so the
 * list is an allowlist of names known to belong to someone else. It costs a
 * line per host app, which is the point: adding one is a deliberate act with
 * evidence behind it, not a widening of the net.
 *
 * `Can't find variable: output` (15, Safari) and `_G` (1) are deliberately
 * NOT here. Too few to matter and not clearly attributable to a host app, so
 * they stay visible rather than being swept up by association.
 */
const IN_APP_BROWSER_BRIDGE_GLOBALS = [
  '_AutofillCallbackHandler', // Meta — Facebook and Instagram autofill bridge
  'CONFIG', // Twitter
  'currentInset', // Twitter
  'SCDynimacBridge', // Snapchat
  'zaloJSV2' // Zalo
];

const IN_APP_BROWSER_BRIDGE_PATTERN = new RegExp(
  `^(Uncaught ")?Can't find variable: (${IN_APP_BROWSER_BRIDGE_GLOBALS.join(
    '|'
  )})"?$`
);

/**
 * Messages from the same source that are not missing-variable errors.
 *
 * `454: Handling is disabled` is Meta's in-app browser refusing to handle a
 * scheme. `Battery Status API is disabled for privacy protection.` is the
 * browser reporting its own policy, which is working as intended and is not
 * something this repo can change.
 */
const IN_APP_BROWSER_MESSAGE_PATTERNS: RegExp[] = [
  /^(Uncaught ")?454: Handling is disabled"?$/,
  /^Battery Status API is disabled for privacy protection\.$/
];

const isInAppBrowserScript = (message: string | undefined): boolean =>
  Boolean(message) &&
  (IN_APP_BROWSER_BRIDGE_PATTERN.test(message as string) ||
    IN_APP_BROWSER_MESSAGE_PATTERNS.some((pattern) =>
      pattern.test(message as string)
    ));

/**
 * FFH-2860. Crawlers that execute JavaScript create RUM sessions, and Datadog
 * records them as `@session.type:user` — so a bot counts as a person in every
 * query, dashboard and monitor denominator.
 *
 * CookieYesbot alone produced 7,608 of the 12,834 `Auth callback failed`
 * errors in the seven days to 3 Sep 2026, across 1,908 sessions: 59% of the
 * single largest error on the site, from one crawler that has no user to
 * affect.
 *
 * Matched on the bot's own token rather than a general /bot/ pattern: real
 * user agents contain the substring in places that have nothing to do with
 * crawling, and losing real sessions to a greedy pattern is a worse failure
 * than keeping a crawler.
 */
const BOT_USER_AGENT_PATTERN =
  /(CookieYesbot|Googlebot|bingbot|AhrefsBot|SemrushBot|YandexBot|DuckDuckBot|Baiduspider|PetalBot|DataForSeoBot|HeadlessChrome|Lighthouse|GTmetrix)/i;

export const isBotUserAgent = (userAgent: string | undefined): boolean =>
  Boolean(userAgent) && BOT_USER_AGENT_PATTERN.test(userAgent as string);

/**
 * Vercel injects the commit SHA when system environment variables are
 * exposed; tagging events with it lets error spikes be correlated to
 * deploys (the Releases section of the ngp-wbr-pue dashboard).
 */
const SHORT_COMMIT_SHA_LENGTH = 7;

const getBuildVersion = (): string | undefined =>
  process.env.NEXT_PUBLIC_VERCEL_GIT_COMMIT_SHA?.slice(
    0,
    SHORT_COMMIT_SHA_LENGTH
  );

class DatadogRumService {
  private initialized = false;
  private hasRequestedForcedReplay = false;

  /**
   * Initialise RUM. Called on every page load regardless of consent so the
   * SDK is available as the error sink (addError). Consent is enforced via
   * `trackingConsent`: while 'not-granted' no data leaves the browser. DNT is
   * enforced by the caller (the orchestrator passes granted=false and never
   * upgrades under DNT).
   *
   * @param consentGranted - current CookieYes analytics consent.
   */
  init(consentGranted: boolean) {
    if (typeof window === 'undefined') return;

    // FFH-2860. Never start a session for a crawler. Returning before init
    // leaves `this.initialized` false, so addError() is a no-op too and a bot
    // contributes to neither the numerator nor the denominator of the error
    // rate — which is the only consistent choice: dropping its errors while
    // keeping its page views would deflate the rate instead of correcting it.
    if (isBotUserAgent(window.navigator?.userAgent)) return;

    if (window.__DATADOG_RUM_INITIALIZED__) {
      this.initialized = true;
      this.reapplyForcedReplay();
      return;
    }

    datadogRum.init({
      applicationId: 'c1f4ded9-ee18-4a0d-b4e7-5e9c09d3ff0c',
      clientToken: 'pub06d75718799e79298f6b0ad59e86e98f',
      site: 'datadoghq.eu',
      service: 'ffh-web-application',
      env: process.env.NEXT_PUBLIC_DATADOG_RUM_ENV,
      version: getBuildVersion(),
      /**
       * SD-662: Measure-by-default. Every session is ingested so Core Web
       * Vitals, page performance and error rates are accurate across all
       * traffic rather than the third of it we used to sample. Cost is not
       * controlled here: ingestion is cheap, and the Datadog retention
       * filters decide server-side what is kept in the expensive
       * Investigate tier. Raising this rate is only safe once those filters
       * are tightened — the two are not independent.
       *
       * Nothing leaves the browser until analytics consent is granted (see
       * trackingConsent below), so this is 100% of CONSENTED sessions.
       */
      sessionSampleRate: 100,
      /**
       * FFH-2736: replay is never recorded by sampling, only where the code
       * asks for it (see useForceReplayOnPaymentRoutes). A recorded session
       * is billed twice, $3.60 per 1,000 to record on top of the $4.50 per
       * 1,000 to index it, and the sampled 5% bought replays of arbitrary
       * pages that no investigation ever asked for: 3,001 of them landed on
       * the homepage alone over the two days to 26 Aug 2026.
       *
       * The manual flag is what makes the 0 an invariant rather than an
       * accident of `performDraw(0)`: the SDK only auto-starts recording for
       * a session it sampled in, so with both set nothing records unless
       * `startSessionReplayRecording({ force: true })` is called.
       */
      sessionReplaySampleRate: 0,
      startSessionReplayRecordingManually: true,
      defaultPrivacyLevel: 'mask-user-input',
      trackUserInteractions: true,
      trackResources: true,
      trackingConsent: consentGranted ? 'granted' : 'not-granted',
      beforeSend: (event) => {
        if (event.type !== 'error') return true;

        if (isKnownNoise(event.error?.message)) return false;
        if (isInAppBrowserScript(event.error?.message)) return false;
        if (isThrownByExtension(event.error?.stack)) return false;
        if (
          isStacklessExtensionTimeout(event.error?.message, event.error?.stack)
        ) {
          return false;
        }

        return true;
      }
    });

    window.__DATADOG_RUM_INITIALIZED__ = true;
    this.initialized = true;
    this.reapplyForcedReplay();
  }

  /**
   * React to a consent change (grant-after-load or revoke). The value is not
   * persisted across page loads, so it must be re-applied on every load via
   * init()'s trackingConsent.
   */
  setConsent(granted: boolean) {
    if (typeof window === 'undefined') return;

    datadogRum.setTrackingConsent(granted ? 'granted' : 'not-granted');

    // Re-apply any forced replay once consent lets RUM actually start: the
    // SDK re-issues a pre-start recording request WITHOUT its options
    // (recorderApi calls strategy.start() bare), so a pre-consent
    // { force: true } would silently degrade to no recording at all.
    if (granted) {
      this.reapplyForcedReplay();
    }
  }

  /**
   * FFH-1932: force session replay recording for the current session. This
   * is the ONLY thing that starts a recording (FFH-2736 set
   * sessionReplaySampleRate to 0), and it is called on the payment routes so
   * every payment failure has a replay to investigate.
   * Safe to call before consent: while trackingConsent is 'not-granted'
   * nothing leaves the browser. The request is sticky: it is re-applied
   * after init() and after a consent grant, because a call deferred by
   * the SDK's pre-start phase loses its force option and would otherwise
   * never record at all.
   */
  forceSessionReplay() {
    if (typeof window === 'undefined') return;

    this.hasRequestedForcedReplay = true;

    if (!this.initialized) return;

    datadogRum.startSessionReplayRecording({ force: true });
  }

  private reapplyForcedReplay() {
    if (!this.hasRequestedForcedReplay) return;

    datadogRum.startSessionReplayRecording({ force: true });
  }

  /**
   * Reporting an error must never itself throw. Most call sites sit inside a
   * `catch` that exists to keep a page alive, or run before a state setter
   * that unblocks the UI: on /thank-you a throw here would skip
   * `setRefreshSettled(true)` and strand a paying customer on a dead button.
   * A lost telemetry event is always cheaper than the failure it reports.
   */
  /**
   * The current RUM session id, or null when nothing is being recorded.
   *
   * Null is the honest answer wherever RUM is not initialised (no consent, SSR,
   * a blocked SDK): a reference shown to a visitor that matches no session is
   * worse than showing none, because support cannot look it up (FFH-2743).
   */
  getSessionId(): string | null {
    if (typeof window === 'undefined' || !this.initialized) return null;

    try {
      return datadogRum.getInternalContext()?.session_id ?? null;
    } catch {
      return null;
    }
  }

  /**
   * FFH-2860. A user event that is NOT a fault.
   *
   * Cancelling a login or being asked to verify an email are expected
   * outcomes, and reporting them through addError made them indistinguishable
   * from a broken sign-in — in error tracking, on the errors dashboard, and in
   * the browser error monitor's numerator. They still need to be countable,
   * because they are funnel steps someone will ask about, so they move here
   * rather than disappearing.
   */
  addAction(name: string, context?: Record<string, any>) {
    if (typeof window === 'undefined' || !this.initialized) return;

    try {
      datadogRum.addAction(name, context);
    } catch {
      // Nothing to do: the reporting channel is the thing that failed.
    }
  }

  addError(error: any, context?: Record<string, any>) {
    if (typeof window === 'undefined' || !this.initialized) return;

    try {
      datadogRum.addError(error, {
        source: 'custom',
        ...context
      });
    } catch {
      // Nothing to do: the reporting channel is the thing that failed.
    }
  }
}

export const datadogRumService = new DatadogRumService();
